import React, { useReducer, useEffect } from "react";
import PropTypes from "prop-types";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { styled } from "@mui/system";
import {
  BORDER_PRIMARY,
  DISPLAY_FLEX,
  FLEX_DIRECTION_COLUMN,
  FLEX_DIRECTION_ROW,
  FLEX_JUSTIFY_CENTER,
  FLEX_JUSTIFY_SPACE_BETWEEN,
  FLEX_JUSTIFY_START,
  MAX_WIDTH,
  TEXT_ALIGN_LEFT,
} from "../config/styles";
import {
  CREATE_STUDENT_CONSTANTS,
  REGEX_VALIDATIONS,
  API_AUTH_TOKEN,
  API_CONTEXT_TYPE,
  API_METHOD_POST,
} from "../config/constants";
import { API_URL, ADMIN_TYPE } from "../utils/Constants";
import { colors } from "../config/colors";
import { SessionExpiredModal } from "./SessionExpiredModal";
import { CustomModal } from "./CustomModal";

export const STUDENT_MODAL_TYPES = {
  CREATE: "create",
  UPDATE: "update",
};

export const CreateModalStyled = styled(Modal)(({ theme }) => ({
  "& .modal-width-half": {
    width: "436px !important",
  },
  "& .modal-header-text": {
    ...theme.typography.body2,
    width: "95%",
    border: "none",
  },
  "& .modal-form input": {
    width: MAX_WIDTH,
  },
  "& .create-button-btn": {
    color: theme.palette.secondary.contrastText,
    backgroundColor: theme.palette.primary.main,
    border: "none",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  "& .edit-button-btn": {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.secondary.contrastText,
    border: BORDER_PRIMARY,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  "& .modal-confirmation": {
    display: DISPLAY_FLEX,
    flexDirection: FLEX_DIRECTION_COLUMN,
    alignItems: FLEX_JUSTIFY_CENTER,
    padding: ".5rem 0 1.5rem 0",
  },
  "& .modal-list-group": {
    textAlign: TEXT_ALIGN_LEFT,
    display: DISPLAY_FLEX,
    flexDirection: FLEX_DIRECTION_COLUMN,
    justifyContent: FLEX_JUSTIFY_START,
    alignItems: FLEX_JUSTIFY_START,
    width: MAX_WIDTH,
  },
  "& .modal-list-group-item": {
    display: DISPLAY_FLEX,
    flexDirection: FLEX_DIRECTION_COLUMN,
    padding: ".5rem 0",
  },
  "& .modal-button-container": {
    display: DISPLAY_FLEX,
    flexDirection: FLEX_DIRECTION_ROW,
    alignItems: FLEX_JUSTIFY_CENTER,
    justifyContent: FLEX_JUSTIFY_SPACE_BETWEEN,
  },
  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
    "-webkit-appearance": "none",
    margin: 0,
  },
  '& input[type="number"]': {
    "-moz-appearance": "textfield",
  },
}));

// Action Types
const ACTION_TYPES = {
  SET_FIELD: "SET_FIELD",
  SET_MULTIPLE_FIELDS: "SET_MULTIPLE_FIELDS",
  RESET_FORM: "RESET_FORM",
  INITIALIZE_FORM: "INITIALIZE_FORM",
  SET_VALIDATION_ERROR: "SET_VALIDATION_ERROR",
  CLEAR_VALIDATION_ERRORS: "CLEAR_VALIDATION_ERRORS",
};

// Initial State
const initialState = {
  // Student Information
  schoolId: "",
  merchantId: "",
  studentId: "",
  schoolName: "",
  firstName: "",
  middleName: "",
  lastName: "",
  schoolPhoneNum: "",
  givenName: "",
  grade: "",
  division: "",
  dateOfBirth: "",
  nationality: "",
  gender: "",

  // Parent Information
  fatherName: "",
  motherName: "",
  parentId: "",
  primaryMobileNum: "",
  secondaryMobileNum: "",
  mobileNum3: "",
  officeNum: "",
  residenceNum: "",
  address: "",
  emailId1: "",
  emailId2: "",
  emailId3: "",
  emergencyContactPerson: "",
  emergencyMobileNum: "",

  // Extra Fields
  extraField1: "",
  extraField2: "",
  extraField3: "",
  extraField4: "",
  extraField5: "",

  // Fee Information
  schoolFees: "",
  tuitionFees: "",
  transportationFees: "",
  canteenFees: "",
  bookFees: "",
  eventFee: "",
  uniformFee: "",
  registrationFees: "",
  admissionFees: "",
  schoolTripeFees: "",
  stationaryFees: "",
  medicalFees: "",
  securityDepositFees: "",

  // Additional Fields
  emiratesId: "",
  productName: "",
  outstandingAmount: "",
  recurringFeesEnabled: false,
  customProducts: "",
  enrollmentFees: "",
  // Fee fields (Fee1-Fee200) - Generated programmatically
  ...Array.from({ length: 200 }, (_, index) => ({
    [`fee${index + 1}`]: "",
  })).reduce((acc, curr) => ({ ...acc, ...curr }), {}),

  // Application State
  isLoading: false,
  updateSuccess: false,
  updateFailure: false,
  addSuccessMsg: "",
  errorMessage: "",
  sessionExpired: false,
  studentPrimaryKey: "",

  // Validation errors
  validationErrors: {},
};

// Reducer Function
const createStudentReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_FIELD:
      return {
        ...state,
        [action.fieldName]: action.value,
        validationErrors: {
          ...state.validationErrors,
          [action.fieldName]: null, // Clear validation error for this field
        },
      };

    case ACTION_TYPES.SET_MULTIPLE_FIELDS:
      return {
        ...state,
        ...action.fields,
      };

    case ACTION_TYPES.INITIALIZE_FORM:
      return {
        ...state,
        ...action.initialData,
      };

    case ACTION_TYPES.RESET_FORM:
      return {
        ...initialState,
      };

    case ACTION_TYPES.SET_VALIDATION_ERROR:
      return {
        ...state,
        validationErrors: {
          ...state.validationErrors,
          [action.fieldName]: action.error,
        },
      };

    case ACTION_TYPES.CLEAR_VALIDATION_ERRORS:
      return {
        ...state,
        validationErrors: {},
      };

    default:
      return state;
  }
};

export const CreateStudentModal = ({
  show,
  handleHide,
  type = STUDENT_MODAL_TYPES.CREATE,
  recordDetails = {},
}) => {
  const [formState, dispatch] = useReducer(createStudentReducer, initialState);
  const isCreateUser = type === STUDENT_MODAL_TYPES.CREATE;

  const getSuccessMessage = isCreateUser
    ? "Student successfully added"
    : "Student data updated successfully";

  useEffect(() => {
    // Initialize form with record details if provided
    if (type === STUDENT_MODAL_TYPES.UPDATE && recordDetails) {
      const initialData = {
        ...initialState,
        ...recordDetails,
        studentPrimaryKey: recordDetails.studentPrimaryKey || "",
      };
      dispatch({
        type: ACTION_TYPES.INITIALIZE_FORM,
        initialData,
      });
    }
  }, [type, recordDetails]);

  const handleConfirmation = async (event) => {
    if (event) event.preventDefault();
    const requiredFields = [
      "schoolName",
      "studentId",
      "firstName",
      "lastName",
      "grade",
      "gender",
    ];
    const emptyField = requiredFields.find((item) => !formState[item]);
    // Validation checks
    if (emptyField) {
      dispatch({
        type: ACTION_TYPES.SET_VALIDATION_ERROR,
        fieldName: emptyField,
        error: "Please update all the mandatory fields with * to continue",
      });
      return;
    }

    if (
      formState.gender !== "m" &&
      formState.gender !== "M" &&
      formState.gender !== "f" &&
      formState.gender !== "F"
    ) {
      dispatch({
        type: ACTION_TYPES.SET_VALIDATION_ERROR,
        fieldName: "gender",
        error: "Gender of the student should be either M or F",
      });
      return;
    }

    // Set loading state
    dispatch({
      type: ACTION_TYPES.SET_FIELD,
      fieldName: "isLoading",
      value: true,
    });

    try {
      let roleData = localStorage.getItem("role");
      let authCode = localStorage.getItem("bearer");
      let merchantIdGlobal = "";

      if (roleData === ADMIN_TYPE.SUPERADMIN) {
        merchantIdGlobal = localStorage.getItem("globalMerchantId");
      }
      if (roleData === ADMIN_TYPE.SCHOOLADMIN) {
        merchantIdGlobal = localStorage.getItem("schoolAdminMerchantId");
      }

      let data = {
        schoolId: merchantIdGlobal,
        studentPrimaryKey: formState.studentPrimaryKey,
        studentId: formState.studentId,
        grade: formState.grade,
        merchantId: merchantIdGlobal,
        schoolPhoneNum: formState.schoolPhoneNum,
        firstName: formState.firstName.trim().replace(/\s+/g, " "),
        middleName: formState.middleName.trim().replace(/\s+/g, " "),
        givenName: formState.givenName.trim().replace(/\s+/g, " "),
        division: formState.division,
        dateOfBirth: formState.dateOfBirth,
        nationality: formState.nationality,
        fatherName: formState.fatherName.trim().replace(/\s+/g, " "),
        motherName: formState.motherName.trim().replace(/\s+/g, " "),
        primaryMobileNum: formState.primaryMobileNum,
        officeNum: formState.officeNum,
        residenceNum: formState.residenceNum,
        secondaryMobileNum: formState.secondaryMobileNum,
        mobileNum3: formState.mobileNum3,
        address: formState.address,
        emailId1: formState.emailId1,
        emailId2: formState.emailId2,
        emailId3: formState.emailId3,
        schoolName: formState.schoolName,
        lastName: formState.lastName.trim().replace(/\s+/g, " "),
        emergencyContactPerson: formState.emergencyContactPerson,
        extraField1: formState.extraField1,
        extraField2: formState.extraField2,
        extraField3: formState.extraField3,
        extraField4: formState.extraField4,
        extraField5: formState.extraField5,
        schoolFees: formState.schoolFees,
        tuitionFees: formState.tuitionFees,
        transportationFees: formState.transportationFees,
        canteenFees: formState.canteenFees,
        bookFees: formState.bookFees,
        eventFee: formState.eventFee,
        uniformFee: formState.uniformFee,
        registrationFees: formState.registrationFees,
        admissionFees: formState.admissionFees,
        schoolTripeFees: formState.schoolTripeFees,
        stationaryFees: formState.stationaryFees,
        medicalFees: formState.medicalFees,
        securityDepositFees: formState.securityDepositFees,
        gender: formState.gender.toUpperCase(),
        studentRegistered: false,
        parentId: formState.parentId,
        emiratesId: formState.emiratesId,
        emergencyMobileNum: formState.emergencyMobileNum,
        productName: formState.productName,
        outstandingAmount: formState.outstandingAmount,
        recurringFeesEnabled: formState.recurringFeesEnabled,
        customProducts: formState.customProducts,
        enrollmentFees: formState.enrollmentFees,
        // All 200 fee fields (Fee1-Fee200)
        ...Array.from({ length: 200 }, (_, index) => ({
          [`fee${index + 1}`]: (formState[`fee${index + 1}`] || "").replace(
            /^0+/g,
            "0"
          ),
        })).reduce((acc, curr) => ({ ...acc, ...curr }), {}),
      };

      const response = await fetch(
        isCreateUser
          ? API_URL.ADD_SINGLE_STUDENT
          : API_URL.UPDATE_SINGLE_STUDENT,
        {
          method: API_METHOD_POST,
          body: JSON.stringify(data),
          headers: {
            "Content-Type": API_CONTEXT_TYPE,
            Authorization: authCode,
            "X-Auth-Token": API_AUTH_TOKEN,
          },
        }
      );

      const auth = response.headers.get("Authorization");
      if (auth) localStorage.setItem("bearer", auth);

      const result = await response.json();

      if (
        result?.error?.status_code === 401 ||
        result?.status === "403" ||
        result?.status?.internalMessage?.includes("403")
      ) {
        dispatch({
          type: ACTION_TYPES.SET_FIELD,
          fieldName: "sessionExpired",
          value: true,
        });
        return;
      }

      if (result?.status?.code === "Y") {
        const successMsg =
          typeof result.body === "string" ? result.body : getSuccessMessage;

        dispatch({
          type: ACTION_TYPES.SET_MULTIPLE_FIELDS,
          fields: {
            isLoading: false,
            updateSuccess: true,
            addSuccessMsg: successMsg,
          },
        });
      }

      if (result.status.code === "N") {
        dispatch({
          type: ACTION_TYPES.SET_MULTIPLE_FIELDS,
          fields: {
            isLoading: false,
            updateFailure: true,
            errorMessage:
              "There was a problem in adding the student record. Please try again later.",
          },
        });
      }
    } catch (error) {
      dispatch({
        type: ACTION_TYPES.SET_MULTIPLE_FIELDS,
        fields: {
          isLoading: false,
          updateFailure: true,
          errorMessage: "Network error. Please try again later.",
        },
      });
    }
  };

  // Reset form
  const resetForm = () => {
    dispatch({ type: ACTION_TYPES.RESET_FORM });
  };

  // Set default values for certain fields
  const setDefaultValues = () => {
    // Get user role and determine merchant ID
    const roleData = localStorage.getItem("role");
    let merchantIdGlobal = "";

    // Set merchant ID based on user role
    if (roleData === ADMIN_TYPE.SUPERADMIN) {
      merchantIdGlobal = localStorage.getItem("globalMerchantId") || "";
    } else if (roleData === ADMIN_TYPE.SCHOOLADMIN) {
      merchantIdGlobal = localStorage.getItem("schoolAdminMerchantId") || "";
    }

    // Define default values
    const defaultValues = {
      schoolId: merchantIdGlobal,
      merchantId: merchantIdGlobal,
    };

    // Apply default values to the form state
    dispatch({
      type: ACTION_TYPES.SET_MULTIPLE_FIELDS,
      fields: defaultValues,
    });
  };

  // Handle modal hide
  const handleModalHide = () => {
    dispatch({ type: ACTION_TYPES.CLEAR_VALIDATION_ERRORS });
    resetForm(); // Reset form when modal is closed
    if (handleHide) {
      handleHide();
    }
  };

  const onSessionExpiredHide = () => {
    dispatch({
      type: ACTION_TYPES.SET_FIELD,
      fieldName: "sessionExpired",
      value: false,
    });
    resetForm(); // Reset form when session expired modal is closed
    if (handleHide) {
      handleHide();
    }
  };

  const onSuccessHide = () => {
    dispatch({
      type: ACTION_TYPES.SET_FIELD,
      fieldName: "updateSuccess",
      value: false,
    });
    resetForm(); // Reset form when success modal is closed
    if (handleHide) {
      handleHide();
    }
  };

  const onFailureHide = () => {
    dispatch({
      type: ACTION_TYPES.SET_FIELD,
      fieldName: "updateFailure",
      value: false,
    });
    resetForm(); // Reset form when failure modal is closed
    if (handleHide) {
      handleHide();
    }
  };

  // Initialize with default values when modal opens
  useEffect(() => {
    if (show) {
      setDefaultValues();
    }
  }, [show]);

  const StudentDetailsList = [
    {
      label: "School ID",
      value: formState.schoolId,
      eventId: "schoolId",
      disabled: true,
      isRequired: true,
      restrictSpace: true,
      type: "text",
    },
    {
      label: "Merchant ID",
      value: formState.merchantId,
      eventId: "merchantId",
      disabled: true,
      isRequired: true,
      restrictSpace: true,
      type: "text",
    },
    {
      label: "Student ID",
      value: formState.studentId,
      eventId: "studentId",
      disabled: !isCreateUser,
      isRequired: true,
      restrictSpace: true,
      regex: "alphaNumeric",
    },
    {
      label: "School Name",
      value: formState.schoolName,
      eventId: "schoolName",
      isRequired: true,
      type: "text",
    },
    {
      label: "First Name",
      value: formState.firstName,
      eventId: "firstName",
      isRequired: true,
      regex: "alphaWithInternationalSpace",
      restrictSpace: true,
      type: "text",
    },
    {
      label: "Middle Name",
      value: formState.middleName,
      eventId: "middleName",
      regex: "alphaWithInternationalSpace",
      restrictSpace: true,
      type: "text",
    },
    {
      label: "Last Name",
      value: formState.lastName,
      eventId: "lastName",
      isRequired: true,
      regex: "alphaWithInternationalSpace",
      restrictSpace: true,
      type: "text",
    },
    {
      label: "School Phone Number",
      value: formState.schoolPhoneNum,
      eventId: "schoolPhoneNum",
      restrictSpace: true,
      type: "number",
    },
    {
      label: "Given Name",
      value: formState.givenName,
      eventId: "givenName",
      regex: "alphaWithInternationalSpace",
      type: "text",
    },
    {
      label: "Grade",
      value: formState.grade,
      eventId: "grade",
      isRequired: true,
      type: "text",
    },
    {
      label: "Division",
      value: formState.division,
      eventId: "division",
      type: "text",
    },
    {
      label: "Date Of Birth (dd/mm/yyyy)",
      value: formState.dateOfBirth,
      eventId: "dateOfBirth",
      regex: "dateProgressive",
      type: "text",
    },
    {
      label: "Nationality",
      value: formState.nationality,
      eventId: "nationality",
      regex: "alpha",
      type: "text",
    },
    {
      label: "Father Name",
      value: formState.fatherName,
      eventId: "fatherName",
      regex: "alphaWithInternationalSpace",
      type: "text",
    },
    {
      label: "Mother Name",
      value: formState.motherName,
      eventId: "motherName",
      regex: "alphaWithInternationalSpace",
      type: "text",
    },
    {
      label: "Parent ID",
      value: formState.parentId,
      eventId: "parentId",
      restrictSpace: true,
      type: "text",
    },
    {
      label: "Parent Mobile Number",
      value: formState.primaryMobileNum,
      eventId: "primaryMobileNum",
      type: "number",
      restrictSpace: true,
    },
    {
      label: "Parent Mobile Number 2",
      value: formState.secondaryMobileNum,
      eventId: "secondaryMobileNum",
      type: "number",
      restrictSpace: true,
    },
    {
      label: "Parent Mobile Number 3",
      value: formState.mobileNum3,
      eventId: "mobileNum3",
      type: "number",
      restrictSpace: true,
    },
    {
      label: "Office Number",
      value: formState.officeNum,
      eventId: "officeNum",
      type: "number",
      restrictSpace: true,
    },
    {
      label: "Residence Number",
      value: formState.residenceNum,
      eventId: "residenceNum",
      type: "number",
      restrictSpace: true,
    },
    {
      label: "Address",
      value: formState.address,
      eventId: "address",
      type: "text",
    },
    {
      label: "Email Id1",
      value: formState.emailId1,
      eventId: "emailId1",
      restrictSpace: true,
      type: "email",
    },
    {
      label: "Email Id2",
      value: formState.emailId2,
      eventId: "emailId2",
      restrictSpace: true,
      type: "email",
    },
    {
      label: "Email Id3",
      value: formState.emailId3,
      eventId: "emailId3",
      restrictSpace: true,
      type: "email",
    },
    {
      label: "Emergency Contact Person",
      value: formState.emergencyContactPerson,
      eventId: "emergencyContactPerson",
      type: "number",
      restrictSpace: true,
    },
    {
      label: "Extra Field1",
      value: formState.extraField1,
      eventId: "extraField1",
      regex: "alpha",
      type: "text",
    },
    {
      label: "Extra Field2",
      value: formState.extraField2,
      eventId: "extraField2",
      regex: "alpha",
      type: "text",
    },
    {
      label: "Extra Field3",
      value: formState.extraField3,
      eventId: "extraField3",
      regex: "alpha",
      type: "text",
    },
    {
      label: "Extra Field4",
      value: formState.extraField4,
      eventId: "extraField4",
      regex: "alpha",
      type: "text",
    },
    {
      label: "Extra Field5",
      value: formState.extraField5,
      eventId: "extraField5",
      regex: "alpha",
      type: "text",
    },
    {
      label: "School Fees",
      value: formState.schoolFees,
      eventId: "schoolFees",
      type: "number",
      restrictSpace: true,
    },
    {
      label: "Tuition Fees",
      value: formState.tuitionFees,
      eventId: "tuitionFees",
      type: "number",
      restrictSpace: true,
    },
    {
      label: "Transportation Fees",
      value: formState.transportationFees,
      eventId: "transportationFees",
      type: "number",
      restrictSpace: true,
    },
    {
      label: "Canteen Fees",
      value: formState.canteenFees,
      eventId: "canteenFees",
      type: "number",
      restrictSpace: true,
    },
    {
      label: "Book Fees",
      value: formState.bookFees,
      eventId: "bookFees",
      type: "number",
      restrictSpace: true,
    },
    {
      label: "Event Fee",
      value: formState.eventFee,
      eventId: "eventFee",
      type: "number",
      restrictSpace: true,
    },
    {
      label: "Uniform Fee",
      value: formState.uniformFee,
      eventId: "uniformFee",
      type: "number",
      restrictSpace: true,
    },
    {
      label: "Registration Fees",
      value: formState.registrationFees,
      eventId: "registrationFees",
      type: "number",
      restrictSpace: true,
    },
    {
      label: "Admission Fees",
      value: formState.admissionFees,
      eventId: "admissionFees",
      type: "number",
      restrictSpace: true,
    },
    {
      label: "School Trip Fees",
      value: formState.schoolTripeFees,
      eventId: "schoolTripeFees",
      type: "number",
      restrictSpace: true,
    },
    {
      label: "Stationary Fees",
      value: formState.stationaryFees,
      eventId: "stationaryFees",
      type: "number",
      restrictSpace: true,
    },
    {
      label: "Medical Fees",
      value: formState.medicalFees,
      eventId: "medicalFees",
      type: "number",
      restrictSpace: true,
    },
    {
      label: "Security Deposit Fees",
      value: formState.securityDepositFees,
      eventId: "securityDepositFees",
      type: "number",
      restrictSpace: true,
    },
    {
      label: "Emirates ID",
      value: formState.emiratesId,
      eventId: "emiratesId",
      type: "text",
    },
    {
      label: "Product Name",
      value: formState.productName,
      eventId: "productName",
      type: "text",
    },
    {
      label: "Outstanding Amount",
      value: formState.outstandingAmount,
      eventId: "outstandingAmount",
      type: "number",
      restrictSpace: true,
    },
    {
      label: "Emergency Mobile Number",
      value: formState.emergencyMobileNum,
      eventId: "emergencyMobileNum",
      type: "number",
    },
    {
      label: "Gender",
      value: formState.gender,
      eventId: "gender",
      isRequired: true,
      regex: "alpha",
      type: "text",
      restrictSpace: true,
    },
    {
      label: "Recurring Fees Enabled",
      value: formState.recurringFeesEnabled,
      eventId: "recurringFeesEnabled",
      type: "text",
      restrictSpace: true,
    },
    {
      label: "Custom Products",
      value: formState.customProducts,
      eventId: "customProducts",
      type: "text",
      restrictSpace: false,
    },
    {
      label: "Enrollment Fees",
      value: formState.enrollmentFees,
      eventId: "enrollmentFees",
      type: "number",
      restrictSpace: true,
    },
    ...Array.from({ length: 200 }, (_, index) => ({
      label: `Fee${index + 1}`,
      value: formState[`fee${index + 1}`],
      eventId: `fee${index + 1}`,
      type: "number",
      restrictSpace: true,
    })),
  ];
  return (
    <CreateModalStyled
      show={show}
      onHide={handleModalHide}
      dialogClassName="modal-width-half"
      aria-labelledby="example-custom-modal-styling-title"
      centered
      scrollable
    >
      <Form className="modal-form" autoComplete="nope">
        <Modal.Header closeButton>
          <Modal.Title
            id="example-custom-modal-styling-title"
            className="modal-header-text"
          >
            {isCreateUser
              ? CREATE_STUDENT_CONSTANTS.HEADER
              : CREATE_STUDENT_CONSTANTS.MODIFY_RECORDS}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "75vh", overflowY: "auto" }}>
          <Form.Group as={Row} className="mx-1" controlId="">
            {StudentDetailsList.map((item) => {
              const handleChangeWithValidation = (event) => {
                let value = event?.target?.value || "";
                let updatedValue = item.restrictSpace
                  ? value.replace(REGEX_VALIDATIONS.removeSpace, "")
                  : value;
                const regexPattern = REGEX_VALIDATIONS[item.regex];
                if (
                  updatedValue === "" ||
                  !item.regex ||
                  !regexPattern ||
                  regexPattern.test(updatedValue)
                ) {
                  dispatch({
                    type: ACTION_TYPES.SET_FIELD,
                    fieldName: item.eventId,
                    value: updatedValue,
                  });
                }
              };

              const hasError = formState.validationErrors[item.eventId];

              return (
                <Form.Floating className="mb-3 px-2" key={item.label}>
                  <Form.Control
                    id={item.eventId}
                    type={item.type || "text"}
                    autoComplete="off"
                    value={item.value || ""}
                    onChange={(e) => {
                      handleChangeWithValidation(e);
                    }}
                    onSelect={(e) => {
                      handleChangeWithValidation(e);
                    }}
                    disabled={item.disabled}
                    isInvalid={hasError}
                    required={item.isRequired}
                  />
                  <label htmlFor={item.eventId}>
                    {item.label}
                    {item.isRequired && (
                      <span style={{ color: colors.warning }}> *</span>
                    )}
                  </label>
                  {hasError && (
                    <Form.Control.Feedback type="invalid">
                      {hasError}
                    </Form.Control.Feedback>
                  )}
                </Form.Floating>
              );
            })}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="create-button-btn w-100"
            onClick={handleConfirmation}
          >
            {isCreateUser
              ? CREATE_STUDENT_CONSTANTS.CREATE_BUTTON
              : CREATE_STUDENT_CONSTANTS.UPDATE_BUTTON}
          </Button>
        </Modal.Footer>
      </Form>
      {formState.sessionExpired ? (
        <SessionExpiredModal
          show={formState.sessionExpired}
          onHide={onSessionExpiredHide}
        />
      ) : null}
      {formState.updateSuccess ? (
        <CustomModal
          show={formState.updateSuccess}
          onHide={onSuccessHide}
          title={formState.addSuccessMsg}
          message={""}
        />
      ) : null}
      {formState.updateFailure ? (
        <CustomModal
          show={formState.updateFailure}
          onHide={onFailureHide}
          title={formState.errorMessage}
          message={""}
        />
      ) : null}
    </CreateModalStyled>
  );
};

CreateStudentModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleHide: PropTypes.func.isRequired,
  type: PropTypes.string,
  recordDetails: PropTypes.object,
};
